import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * Why the reading position changed.
 *
 * epub.js only tells us *that* the player relocated, never *why*. Each control sets its
 * trigger here before navigating, and the next location analytics event picks it up.
 *
 * Values are named after the control as it exists in the UI, not after the direction of
 * travel: in RTL books the LEFT chevron moves the reader forward. Direction through the
 * book is derivable downstream from the displayedRangeStart delta between events.
 *
 * Jumps (SLIDER, TOC, BOOKMARK, ...) carry no direction — for those, "forwards" is a
 * property of where the reader happened to be standing, not of the action.
 */
export type NavigationTrigger =
  // Steps — the control itself is directional
  | 'CHEVRON_LEFT'
  | 'CHEVRON_RIGHT'
  | 'SWIPE_LEFT'
  | 'SWIPE_RIGHT'
  | 'KEYBOARD_LEFT'
  | 'KEYBOARD_RIGHT'
  | 'SKIP_BUTTON_PREVIOUS'
  | 'SKIP_BUTTON_NEXT'
  | 'LOCK_SCREEN_PREVIOUS'
  | 'LOCK_SCREEN_NEXT'
  | 'TIME_SKIP_FORWARD'
  | 'TIME_SKIP_BACK'
  // Jumps
  | 'SLIDER'
  | 'THUMBNAIL'
  | 'TOC'
  | 'PAGE_LIST'
  | 'PAGE_INPUT'
  | 'PLAYLIST'
  | 'BOOKMARK'
  | 'ANNOTATION'
  | 'TEXT_CLICK'
  | 'GO_TO_START'
  | 'GO_TO_PLAYBACK'
  // The player moved the reading on by itself
  | 'AUTOMATIC_PAGE_TURN'
  | 'AUTOMATIC_TRACK_ADVANCE'
  // The position moved for a reason that isn't reading
  | 'DEEPLINK'
  | 'RESTORE_POSITION'
  | 'RE_RENDERING';

/**
 * A trigger only describes the location event that follows it. Navigations that never
 * produce one — a chevron press at the end of the book, a goto that lands where we
 * already are — would otherwise leave their trigger behind to be picked up by an
 * unrelated relocation much later.
 *
 * Keep this comfortably above the slowest path from control to location event. The longest
 * is the automatic page turn: AudioPlayer sets the trigger, MediaPlayer then waits
 * AUTO_PAGE_TURN_DELAY_MS (2s) before navigating, and the page flip and relocation follow.
 */
const TRIGGER_MAX_AGE_MS = 5000;

const useNavigationTriggerStore = defineStore('navigation-trigger', () => {
  const pendingTrigger = ref<{ trigger: NavigationTrigger; setAt: number } | null>(null);

  /**
   * Record why we are about to navigate. Call this from the outermost handler — the
   * component's click/gesture/key handler — never from inside goToNext()/goToContent(),
   * which every control funnels through and where the origin is already lost.
   */
  const setNavigationTrigger = (trigger: NavigationTrigger) => {
    pendingTrigger.value = { trigger, setAt: Date.now() };
  };

  /**
   * Read and clear. Two quick taps on the same control count as one action: the first
   * location event claims the trigger, the rest report none.
   */
  const takeNavigationTrigger = (): NavigationTrigger | undefined => {
    const pending = pendingTrigger.value;
    pendingTrigger.value = null;

    if (!pending || Date.now() - pending.setAt > TRIGGER_MAX_AGE_MS) {
      return undefined;
    }

    return pending.trigger;
  };

  const $reset = () => {
    pendingTrigger.value = null;
  };

  return {
    setNavigationTrigger,
    takeNavigationTrigger,
    $reset,
  };
});

export default useNavigationTriggerStore;
